// =============================================================================
// PipelineDistributionFunnel — Répartition des dossiers sur le pipeline cible
// Source : GET /api/governance/pipeline/distribution (aucun hardcode)
// =============================================================================
import { useEffect, useState, useCallback } from "react";
import { RefreshCw, AlertCircle } from "lucide-react";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Mapping couleur sémantique → hex (visuel uniquement, non-métier)
const COLOR_HEX = {
  slate: "#64748b",
  blue: "var(--oh-stat-blue-text)",
  cyan: "var(--oh-stat-cyan-text)",
  indigo: "#6366f1",
  violet: "#8b5cf6",
  purple: "var(--oh-stat-purple-text)",
  amber: "var(--oh-stat-amber-text)",
  orange: "var(--oh-stat-orange-text)",
  green: "#10b981",
  red: "var(--oh-stat-red-text)",
};

export default function PipelineDistributionFunnel({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sansPipeline, setSansPipeline] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSansPipeline(false);
    try {
      const token = localStorage.getItem("session_token");
      const headers = {
        // Sans tenant explicite : AUCUN en-tête, le serveur prend le tenant de la session.
        ...(tenantId && { ...enteteTenant(tenantId) }),
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      const res = await fetch(`${API}/api/governance/pipeline/distribution`, {
        headers, credentials: "include",
      });
      if (res.status === 404) { setSansPipeline(true); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div
        className="rounded-lg border p-6 flex items-center justify-center"
        style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)" }}
        data-testid="pipeline-dist-loading"
      >
        <RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
        <span className="ml-2 text-sm" style={{ color: "var(--oh-text-muted)" }}>
          Chargement de la répartition…
        </span>
      </div>
    );
  }

  // 404 = ce tenant n'a aucun pipeline gouverné : rien à répartir, on ne montre pas d'erreur.
  if (sansPipeline) return null;

  if (error || !data) {
    return (
      <div
        className="rounded-lg border p-4 flex items-center gap-2"
        style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)" }}
        data-testid="pipeline-dist-error"
      >
        <AlertCircle className="w-4 h-4 text-red-400" />
        <span className="text-sm" style={{ color: "var(--oh-text-muted)" }}>
          Impossible de charger la répartition : {error || "inconnu"}
        </span>
      </div>
    );
  }

  const { total, total_assigned, steps } = data;
  const linear = steps.filter((s) => !s.is_side);
  const side = steps.filter((s) => s.is_side);
  const maxCount = Math.max(1, ...linear.map((s) => s.count));

  // Détection des goulots d'étranglement : étape avec >25% du total assigned
  const bottleneckThreshold = total_assigned * 0.25;
  const bottlenecks = linear.filter((s) => s.count > bottleneckThreshold);

  return (
    <div
      className="rounded-lg border p-4"
      style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)" }}
      data-testid="pipeline-distribution-funnel"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2
            className="text-base font-semibold"
            style={{ color: "var(--oh-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Répartition sur le pipeline cible
          </h2>
          <p className="text-xs mt-0.5" style={{ color: "var(--oh-text-muted)" }}>
            Source : governance_pipeline · {total_assigned} dossier(s) positionné(s){total !== total_assigned && ` sur ${total}`}
          </p>
        </div>
        <button
          type="button"
          onClick={loadData}
          className="p-1.5 rounded hover:bg-white/5 transition-colors"
          data-testid="pipeline-dist-refresh"
          title="Rafraîchir"
        >
          <RefreshCw className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
        </button>
      </div>

      {/* Goulots d'étranglement détectés */}
      {bottlenecks.length > 0 && (
        <div
          className="mb-3 rounded px-3 py-2 flex items-start gap-2"
          style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)" }}
          data-testid="pipeline-dist-bottleneck"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--oh-stat-amber-text)" }} />
          <div className="text-xs" style={{ color: "#fbbf24" }}>
            <span className="font-medium">Concentration détectée :</span>{" "}
            {bottlenecks.map((b, i) => (
              <span key={b.code}>
                {b.label} ({b.count} · {b.pct}%){i < bottlenecks.length - 1 ? " · " : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Étapes linéaires (1..9) */}
      <div className="space-y-1.5" data-testid="pipeline-dist-steps-linear">
        {linear.map((step) => {
          const widthPct = Math.max((step.count / maxCount) * 100, step.count > 0 ? 8 : 3);
          const color = COLOR_HEX[step.color] || "#64748b";
          const isEmpty = step.count === 0;
          return (
            <div
              key={step.code}
              className="flex items-center gap-3"
              data-testid={`pipeline-dist-step-${step.code}`}
            >
              <span
                className="text-[10px] w-6 text-right font-mono font-bold shrink-0"
                style={{ color: "var(--oh-text-muted)" }}
              >
                {step.order}
              </span>
              <span
                className="text-xs w-24 text-right shrink-0 font-medium"
                style={{ color: isEmpty ? "var(--oh-text-muted)" : "var(--oh-text)" }}
              >
                {step.label}
              </span>
              <div className="flex-1 relative h-7">
                <div
                  className="h-full rounded-r flex items-center px-2 transition-all duration-500"
                  style={{
                    width: `${widthPct}%`,
                    background: isEmpty ? "var(--oh-border)" : color,
                    minWidth: "42px",
                    opacity: isEmpty ? 0.4 : 1,
                  }}
                >
                  <span
                    className="text-[11px] font-bold whitespace-nowrap"
                    style={{ color: isEmpty ? "var(--oh-text-muted)" : "#fff" }}
                  >
                    {step.count} ({step.pct}%)
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Étape latérale (Rejeté/Abandonné) — présentée séparément */}
      {side.length > 0 && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--oh-border)" }} data-testid="pipeline-dist-steps-side">
          {side.map((step) => {
            const color = COLOR_HEX[step.color] || "var(--oh-stat-red-text)";
            const isEmpty = step.count === 0;
            return (
              <div key={step.code} className="flex items-center gap-3">
                <span
                  className="text-[10px] w-6 text-right font-mono font-bold shrink-0"
                  style={{ color }}
                >
                  {step.order}
                </span>
                <span
                  className="text-xs w-24 text-right shrink-0 font-medium"
                  style={{ color: isEmpty ? "var(--oh-text-muted)" : color }}
                >
                  {step.label}
                </span>
                <div className="flex-1 relative h-7">
                  <div
                    className="h-full rounded flex items-center px-2"
                    style={{
                      width: `${Math.max((step.count / maxCount) * 100, step.count > 0 ? 8 : 3)}%`,
                      background: isEmpty ? "var(--oh-border)" : `${color}30`,
                      border: `1px solid ${color}`,
                      minWidth: "42px",
                      opacity: isEmpty ? 0.4 : 1,
                    }}
                  >
                    <span
                      className="text-[11px] font-bold whitespace-nowrap"
                      style={{ color: isEmpty ? "var(--oh-text-muted)" : color }}
                    >
                      {step.count} ({step.pct}%)
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
